import findLastIndex from 'lodash/findLastIndex'
import type { Map as MapboxMap } from 'mapbox-gl'

import {
  DELIVERY_PINS_LAYER,
  ITEM_EDITOR_ARROW_LINE_LAYER,
  ITEM_EDITOR_SHAPE_FILL_LAYER,
  ITEM_EDITOR_SHAPE_LINE_LAYER,
  LOCATION_MARKERS_LAYER,
  LOCATION_MARKERS_SELECTED_LAYER,
  locationMarkerLayerIds,
  MAP_FILE_OVERLAY_LAYER_PREFIXES,
  MARKUP_EDIT_LAYER_IDS,
  pinTwinLayerIds,
  SHAPE_ARROW_LINE_LAYER,
  SHAPE_FILL_LAYER,
  SHAPE_LINE_LAYER,
  SHARED_PINS_CHIP_LAYER,
  SHARED_PINS_CHIP_SELECTED_LAYER,
  SHARED_PINS_LAYER,
  WORKFLOW_PIN_LABELS_ANCHORED_LAYER,
  WORKFLOW_PIN_LABELS_LAYER,
  WORKFLOW_PIN_LABELS_SELECTED_ANCHORED_LAYER,
  WORKFLOW_PIN_LABELS_SELECTED_LAYER,
  WORKFLOW_PINS_LAYER,
} from './mapboxConstants'
import { markupTextLayerIds, SESSION_TEXT_LAYER_ID } from './markupTextLayerIds'

const SHAPE_LAYER_IDS = [
  SHAPE_FILL_LAYER,
  SHAPE_LINE_LAYER,
  SHAPE_ARROW_LINE_LAYER,
]

// The resting MarkupLayer instances. The draw controller's own layers share the `markup-` stem
// (`markup-edit-*`, `markup-active-*`…), so match the instance prefixes, not the stem.
const MARKUP_RENDER_PREFIXES = [
  'markup-display-',
  'markup-draft-',
  'markup-session-',
  'markup-ref-',
]
const TERRA_DRAW_LAYER_PREFIX = 'td-'

// Bottom to top. Placement runs topmost-first, so the block sits directly above the topmost pin,
// and a selected pin above its own selected label.
export const SELECTED_TIER_LAYER_IDS = [
  ...locationMarkerLayerIds(LOCATION_MARKERS_SELECTED_LAYER),
  WORKFLOW_PIN_LABELS_SELECTED_ANCHORED_LAYER,
  WORKFLOW_PIN_LABELS_SELECTED_LAYER,
  SHARED_PINS_CHIP_SELECTED_LAYER,
  ...pinTwinLayerIds(WORKFLOW_PINS_LAYER),
  ...pinTwinLayerIds(DELIVERY_PINS_LAYER),
  ...pinTwinLayerIds(SHARED_PINS_LAYER),
]

const PIN_ICON_PREFIXES = [
  WORKFLOW_PINS_LAYER,
  SHARED_PINS_LAYER,
  DELIVERY_PINS_LAYER,
]

function isSelectedTierLayer(layerId: string): boolean {
  return SELECTED_TIER_LAYER_IDS.includes(layerId)
}

// The chip matches the `shared-pins` prefix but is a caption, not a pin.
function isPinIconLayer(layerId: string): boolean {
  return (
    !isSelectedTierLayer(layerId) &&
    layerId !== SHARED_PINS_CHIP_LAYER &&
    PIN_ICON_PREFIXES.some(prefix => layerId.startsWith(prefix))
  )
}

const RESTING_WORKFLOW_PIN_IDS = [
  WORKFLOW_PINS_LAYER,
  `${WORKFLOW_PINS_LAYER}-counted`,
]
const RESTING_SHARED_PIN_IDS = [
  SHARED_PINS_LAYER,
  `${SHARED_PINS_LAYER}-counted`,
]

export function isMarkupRenderLayer(layerId: string): boolean {
  return MARKUP_RENDER_PREFIXES.some(prefix => layerId.startsWith(prefix))
}

// The markup being edited: the draw controller's chrome and terra-draw's own layers.
export function isMarkupEditLayer(layerId: string): boolean {
  return (
    layerId.startsWith(TERRA_DRAW_LAYER_PREFIX) ||
    MARKUP_EDIT_LAYER_IDS.includes(layerId)
  )
}

// The resting location symbols, matched exactly: the selected tier's twins share the prefix and
// sit above the pins.
const LOCATION_LAYER_IDS = locationMarkerLayerIds(LOCATION_MARKERS_LAYER)

// The symbol layers that caption something else, bottom to top. Below every location symbol, so
// pins, circles and names all place first (STX1-1264). The anchored label places last because it
// places around a location's name; pin labels outrank markup labels by design.
const CAPTION_LAYER_IDS = [
  WORKFLOW_PIN_LABELS_ANCHORED_LAYER,
  'markup-display-labels',
  'markup-draft-labels',
  'markup-session-labels',
  'markup-ref-labels',
  SHARED_PINS_CHIP_LAYER,
  WORKFLOW_PIN_LABELS_LAYER,
]

// The edited item's own artwork, as opposed to the handles used to manipulate it.
const EDITOR_SHAPE_LAYERS = [
  ITEM_EDITOR_SHAPE_FILL_LAYER,
  ITEM_EDITOR_SHAPE_LINE_LAYER,
  ITEM_EDITOR_ARROW_LINE_LAYER,
  SHAPE_ARROW_LINE_LAYER,
]

function isLocationLayer(layerId: string): boolean {
  return LOCATION_LAYER_IDS.includes(layerId)
}

// Workflow pins place before shared ones, the order a click resolves them in. Mounting shared
// first gives that; this holds it when the shared source is re-added on top.
export function getSharedPinsToLowerBelowWorkflowPins(
  orderedLayerIds: string[],
): { anchorId: string; sharedIds: string[] } | null {
  const workflowIndex = orderedLayerIds.findIndex(id =>
    RESTING_WORKFLOW_PIN_IDS.includes(id),
  )
  if (workflowIndex === -1) {
    return null
  }

  const sharedIds = RESTING_SHARED_PIN_IDS.filter(id =>
    orderedLayerIds.includes(id),
  )
  return sharedIds.some(id => orderedLayerIds.indexOf(id) > workflowIndex)
    ? { anchorId: orderedLayerIds[workflowIndex], sharedIds }
    : null
}

// The twins mount with their own sources, under whatever pins mounted later, and react-map-gl
// re-adds a source on top after a style reload. Settled only when the tier follows the topmost
// pin exactly, in canonical order.
export function getSelectedTierToRaiseAbovePins(
  orderedLayerIds: string[],
): { tierIds: string[]; beforeId: string | undefined } | null {
  const tierIds = SELECTED_TIER_LAYER_IDS.filter(id =>
    orderedLayerIds.includes(id),
  )
  const topPinIndex = findLastIndex(orderedLayerIds, isPinIconLayer)
  if (!tierIds.length || topPinIndex === -1) {
    return null
  }

  const above = orderedLayerIds.slice(topPinIndex + 1)
  if (tierIds.every((id, index) => above[index] === id)) {
    return null
  }

  return {
    tierIds,
    beforeId: above.find(id => !isSelectedTierLayer(id)),
  }
}

// Captions sit directly under the lowest location layer, or the lowest pin when that is lower,
// so no caption places before a location or a pin. Markup geometry goes directly under them.
function captionAnchorIndex(orderedLayerIds: string[]): number {
  const anchors = [
    orderedLayerIds.findIndex(isLocationLayer),
    orderedLayerIds.findIndex(isPinIconLayer),
  ].filter(index => index !== -1)
  return anchors.length ? Math.min(...anchors) : -1
}

// Captions mount with their pins and markups, above the locations, so they placed before the
// names and circles and culled them. Placement runs topmost-first, so "after" means "lower":
// settled only when the captions sit directly under the lowest location layer, in canonical
// order. Under the lowest pin when that is lower — no locations on the host, or a location source
// re-added on top — so a caption can never rise above a pin.
export function getCaptionLayersToLowerBelowLocations(
  orderedLayerIds: string[],
): { anchorId: string; captionIds: string[] } | null {
  const captionIds = CAPTION_LAYER_IDS.filter(id =>
    orderedLayerIds.includes(id),
  )
  const anchorIndex = captionAnchorIndex(orderedLayerIds)
  if (!captionIds.length || anchorIndex === -1) {
    return null
  }

  const below = orderedLayerIds.slice(
    Math.max(0, anchorIndex - captionIds.length),
    anchorIndex,
  )
  if (captionIds.every((id, index) => below[index] === id)) {
    return null
  }

  return { anchorId: orderedLayerIds[anchorIndex], captionIds }
}

// The item being edited draws its own fill, line and arrows on the editor's layers, and those
// mount AFTER GlobeViewItems — so they land above the location markers and paint over the very
// item they belong to. Deselected the same shape sits below its marker, because the bulk shape
// layers do; selecting it should not move the label under the fill.
//
// Fill, line and arrows only. The editable POINTS stay on top: a handle you cannot see is one
// you cannot grab.
export function getEditorShapeLayersToLowerBelowLocations(
  orderedLayerIds: string[],
): { anchorId: string; shapeIds: string[] } | null {
  const anchorIndex = orderedLayerIds.findIndex(isLocationLayer)
  if (anchorIndex === -1) {
    return null
  }

  const shapeIds = orderedLayerIds.filter(
    (id, index) => index > anchorIndex && EDITOR_SHAPE_LAYERS.includes(id),
  )

  return shapeIds.length
    ? { anchorId: orderedLayerIds[anchorIndex], shapeIds }
    : null
}

// Markup geometry is drawing, not collision, so no rule hides it. It sits under the captions,
// because style order is draw order too: a label or chip under a markup fill shows through it,
// or vanishes under an opaque one. Enforced rather than mounted in order because react-map-gl
// appends with no beforeId and MarkupLayer's subtree only mounts once markups arrive. Geometry
// only: the markup code labels are captions. The edit layers join it once released — a draft
// deselected mid-session is still drawn by them, not by its resting layer — and while editing
// are raised to the top instead (getMarkupEditLayersToRaiseToTop).
export function getMarkupLayersToLowerBelowCaptions(
  orderedLayerIds: string[],
  isMarkupEditing: boolean,
): { anchorId: string; markupIds: string[] } | null {
  const anchorIndex = captionAnchorIndex(orderedLayerIds)
  if (anchorIndex === -1) {
    return null
  }

  const markupIds = orderedLayerIds.filter(
    (id, index) =>
      index > anchorIndex &&
      (isMarkupRenderLayer(id) ||
        (!isMarkupEditing && isMarkupEditLayer(id))) &&
      !CAPTION_LAYER_IDS.includes(id),
  )

  return markupIds.length
    ? { anchorId: orderedLayerIds[anchorIndex], markupIds }
    : null
}

export function isMapFileOverlayLayer(layerId: string): boolean {
  return MAP_FILE_OVERLAY_LAYER_PREFIXES.some(prefix =>
    layerId.startsWith(prefix),
  )
}

// Overlays above the lowest shape layer; anchoring there keeps editor/collision layers on top.
export function getOverlayLayersToLowerBelowShapes(
  orderedLayerIds: string[],
): { anchorId: string; overlayIds: string[] } | null {
  const anchorIndex = orderedLayerIds.findIndex(id =>
    SHAPE_LAYER_IDS.includes(id),
  )
  if (anchorIndex === -1) {
    return null
  }

  const overlayIds = orderedLayerIds.filter(
    (id, index) => index > anchorIndex && isMapFileOverlayLayer(id),
  )

  return overlayIds.length
    ? { anchorId: orderedLayerIds[anchorIndex], overlayIds }
    : null
}

// Nothing distracts from the markup being edited, so it draws and hit-tests above every pin,
// marker and caption. Once released it goes back under the captions with the markup geometry.
// Moved in their current order: the draw controller keeps its own order among them.
export function getMarkupEditLayersToRaiseToTop(
  orderedLayerIds: string[],
): string[] | null {
  const editIds = orderedLayerIds.filter(isMarkupEditLayer)
  const top = orderedLayerIds.slice(orderedLayerIds.length - editIds.length)
  return editIds.length && !top.every(isMarkupEditLayer) ? editIds : null
}

// getStyle().layers is the root style; style.order is the merged order moveLayer ignores.
function orderedLayerIds(map: MapboxMap): string[] | null {
  try {
    return map.getStyle()?.layers?.map(layer => layer.id) ?? null
  } catch {
    return null
  }
}

// No anchor moves the layers to the top. Mirrors each move into `ids`, so the next rule plans
// against the order this one left behind.
function moveAllBefore(
  map: MapboxMap,
  ids: string[],
  movedIds: string[],
  anchorId: string | undefined,
): void {
  if (anchorId && !map.getLayer(anchorId)) {
    return
  }
  movedIds.forEach(id => {
    if (map.getLayer(id)) {
      map.moveLayer(id, anchorId)
      ids.splice(ids.indexOf(id), 1)
      ids.splice(anchorId ? ids.indexOf(anchorId) : ids.length, 0, id)
    }
  })
}

// Map File overlays re-add above the shape layers after a view switch (STX1-882). Move them
// back beneath the lowest shape — not shapes to the top, which would bury editor layers.
//
// One pass settles: each rule reads the order the previous ones left. The captions go last of the
// lowering rules: they must sit DIRECTLY under their anchor, where the editor shapes and the
// markup geometry are inserted too, so those end up beneath them. The markup being edited is
// raised after everything, so no later move can bury it.
export function enforceShapeLayerOrder(
  map: MapboxMap,
  isMarkupEditing: boolean,
): void {
  const ids = orderedLayerIds(map)
  if (!ids) {
    return
  }

  const shared = getSharedPinsToLowerBelowWorkflowPins(ids)
  if (shared) {
    moveAllBefore(map, ids, shared.sharedIds, shared.anchorId)
  }

  const tier = getSelectedTierToRaiseAbovePins(ids)
  if (tier) {
    moveAllBefore(map, ids, tier.tierIds, tier.beforeId)
  }

  const plan = getOverlayLayersToLowerBelowShapes(ids)
  if (plan) {
    moveAllBefore(map, ids, plan.overlayIds, plan.anchorId)
  }

  const editorShapes = getEditorShapeLayersToLowerBelowLocations(ids)
  if (editorShapes) {
    moveAllBefore(map, ids, editorShapes.shapeIds, editorShapes.anchorId)
  }

  const markups = getMarkupLayersToLowerBelowCaptions(ids, isMarkupEditing)
  if (markups) {
    moveAllBefore(map, ids, markups.markupIds, markups.anchorId)
  }

  const captions = getCaptionLayersToLowerBelowLocations(ids)
  if (captions) {
    moveAllBefore(map, ids, captions.captionIds, captions.anchorId)
  }

  const editing = isMarkupEditing && getMarkupEditLayersToRaiseToTop(ids)
  if (editing) {
    moveAllBefore(map, ids, editing, undefined)
  }

  placeMarkupTextLayers(map, ids)
}

// Text markups sit at the top of the markup geometry, directly under the lowest caption, in mount
// order. They are custom layers, absent from getStyle().layers, so they are placed against the
// merged draw order instead; moveLayer only runs when they are out of place, so the styledata
// pass that calls this settles.
export function placeMarkupTextLayers(
  map: MapboxMap,
  ids: string[] | null = orderedLayerIds(map),
): void {
  const live = markupTextLayerIds(map).filter(id => map.getLayer(id))
  if (!live.length || !ids) {
    return
  }
  const style = (map as unknown as { style?: { order?: string[] } }).style
  const lastEditId = ids.filter(isMarkupEditLayer).pop()
  // The editor's own resting text (the session mount's) follows its own geometry, as its DOM
  // boxes did: directly above the last edit layer, so it rises over the pins with them while
  // editing and sinks back with them when idle.
  const sessionId =
    lastEditId && live.includes(SESSION_TEXT_LAYER_ID)
      ? SESSION_TEXT_LAYER_ID
      : null
  const resting = live.filter(id => id !== sessionId)

  const anchorId =
    CAPTION_LAYER_IDS.find(id => ids.includes(id)) ??
    ids[captionAnchorIndex(ids)]
  if (resting.length && anchorId) {
    const order = style?.order
    const at = order?.indexOf(anchorId) ?? -1
    const isSettled =
      at !== -1 &&
      resting.every((id, i) => order[at - resting.length + i] === id)
    if (!isSettled) resting.forEach(id => map.moveLayer(id, anchorId))
  }

  if (sessionId) {
    const order = style?.order ?? []
    const nextId = order[order.indexOf(lastEditId) + 1]
    if (nextId !== sessionId) map.moveLayer(sessionId, nextId)
  }
}
